import * as React from 'react'
import ChatbotWrapper from '../../../components/ChatbotWrapper'
import Section from '../../../components/Section'
import styles from './styles.module.scss'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { faBolt, faWrench, faShield, faArrowsRotate } from '@fortawesome/free-solid-svg-icons'
import DiscordLink from '../../../components/DiscordLink'
import DownloadAndDiscordButtons from '../../../components/DownloadAndDiscordButtons'

type FAQ = {
  q: string
  a: React.ReactNode
}

type FAQGroup = {
  id: string
  title: string
  icon: IconDefinition
  intro?: string
  faqs: FAQ[]
}

const FAQItem: React.FC<FAQ> = ({ q, a }) => {
  return (
    <details className={styles.faqItem}>
      <summary className={styles.faqSummary}>{q}</summary>
      <div className={styles.faqAnswer}>{a}</div>
    </details>
  )
}

const FAQGroupView: React.FC<FAQGroup> = ({ id, title, icon, intro, faqs }) => {
  return (
    <Section id={id} title={title} icon={<FontAwesomeIcon icon={icon as IconDefinition} className={styles.icon} />}>
      {intro ? <p>{intro}</p> : null}
      <div className={styles.faqList}>
        {faqs.map((f) => (
          <FAQItem key={f.q} q={f.q} a={f.a} />
        ))}
      </div>
    </Section>
  )
}

const FAQS: FAQGroup[] = [
  {
    id: 'stability',
    title: 'Stability & Performance',
    icon: faBolt,
    faqs: [
      {
        q: 'Does Draco.Bot run locally or in the cloud?',
        a: (
          <>
            <p>Draco.Bot is a desktop application that runs locally on your machine. This helps avoid cloud latency and keeps automations responsive during live streams.</p>
          </>
        ),
      },
      {
        q: 'Will Draco.Bot affect my stream performance?',
        a: (
          <>
            <p>Draco.Bot is designed to stay lightweight and responsive during long streams.</p>
            <p>It avoids browser-based automation and uses internal queuing so actions don&apos;t overlap or block each other unexpectedly.</p>
          </>
        ),
      },
      {
        q: 'What happens if Twitch or OBS disconnects?',
        a: (
          <>
            <p>Draco.Bot automatically attempts to reconnect to Twitch and OBS when a connection is lost.</p>
            <p>If a feature is temporarily unavailable, actions are safely skipped rather than blocking everything else.</p>
          </>
        ),
      },
    ],
  },
  {
    id: 'safety',
    title: 'Reliability & Safety',
    icon: faShield,
    faqs: [
      {
        q: 'What happens if something goes wrong mid-stream?',
        a: (
          <>
            <p>Draco.Bot includes configuration backups and recovery to protect your setup.</p>
            <p>Actions are queued and isolated so a failure in one action doesn&apos;t stop others from running.</p>
          </>
        ),
      },
      {
        q: 'Is this safe to use on a live stream?',
        a: (
          <>
            <p>Yes - Draco.Bot is designed for real streams and long sessions.</p>
            <p>Most users run it continuously while streaming.</p>
          </>
        ),
      },
    ],
  },
  {
    id: 'setup',
    title: 'Setup & Ease of Use',
    icon: faWrench,
    faqs: [
      {
        q: 'Do I need to be technical to use Draco.Bot?',
        a: (
          <>
            <p>No. Most features are configured through the UI using dropdowns and actions.</p>
            <p>Advanced features like conditions and queue groups are optional - you only need them if you want more control.</p>
          </>
        ),
      },
      {
        q: 'Is this meant to replace other bots?',
        a: (
          <>
            <p>For many users, yes.</p>
            <p>Draco.Bot can replace common chatbot, OBS automation, and audio tools by combining them into one application.</p>
          </>
        ),
      },
      {
        q: 'Do I need a separate bot account?',
        a: (
          <>
            <p>No. Draco.Bot can operate using your broadcaster account or a dedicated bot account - whichever you prefer.</p>
          </>
        ),
      },
    ],
  },
  {
    id: 'updates',
    title: 'Twitch Changes, Updates & Alpha',
    icon: faArrowsRotate,
    faqs: [
      {
        q: 'Is Draco.Bot still in alpha?',
        a: (
          <>
            <p>Yes. Draco.Bot is in public alpha and actively developed based on real streamer feedback.</p>
            <p>Updates focus on stability, usability, and real-world streaming needs.</p>
          </>
        ),
      },
      {
        q: 'What happens when Twitch changes their APIs?',
        a: (
          <>
            <p>Draco.Bot uses Twitch&apos;s EventSub system for real-time events.</p>
            <p>Updates are released as needed to keep up with Twitch platform changes.</p>
          </>
        ),
      },
      {
        q: 'What platforms are supported?',
        a: (
          <>
            <p>Draco.Bot currently runs on Windows.</p>
            <p>For OBS integration, OBS Studio v28 or newer is required.</p>
          </>
        ),
      },
    ],
  },
]

const FAQs: React.FC = () => {
  return (
    <ChatbotWrapper
      decorate
      containerWrap={false}
      title='FAQs'
      titleButtons={
        <div>
          <a href='#stability' className={styles.iconButton} title='Stability & Performance'>
            <FontAwesomeIcon icon={faBolt as IconDefinition} />
          </a>
          <a href='#safety' className={styles.iconButton} title='Reliability & Safety'>
            <FontAwesomeIcon icon={faShield as IconDefinition} />
          </a>
          <a href='#setup' className={styles.iconButton} title='Setup & Ease of Use'>
            <FontAwesomeIcon icon={faWrench as IconDefinition} />
          </a>
          <a href='#updates' className={styles.iconButton} title='Updates & Alpha'>
            <FontAwesomeIcon icon={faArrowsRotate as IconDefinition} />
          </a>
        </div>
      }
      header={
        <>
          <p>Answers to common questions about stability, setup, and how Draco.Bot behaves during real streams.</p>
          <p>
            If you don&apos;t see your question here, drop by the <DiscordLink /> - we&apos;re happy to help.
          </p>
          <DownloadAndDiscordButtons />
        </>
      }
    >
      <div className={styles.container}>
        {FAQS.map((g) => (
          <FAQGroupView key={g.id} {...g} />
        ))}
      </div>
    </ChatbotWrapper>
  )
}

export default FAQs
